import type { FetchError } from 'ofetch'
import { useLogger } from '#layers/base/composables/useLogger'

interface AuthRetryDeps {
  /** Refreshes the auth state (e.g. the user store `reloadAuth`). Should be single-flight. */
  reloadAuth: () => Promise<unknown>
  /** Reads the current access token *after* a refresh. Re-read on every call. */
  getAccessToken: () => string | null | undefined
  log: { debug: (message: string, ...args: unknown[]) => void }
}

/**
 * Dependency-injected auth-retry core: runs `call()` and, on HTTP 401,
 * refreshes auth once and retries. Re-throws if the refresh fails.
 * Free of Nuxt/Pinia coupling for reuse and unit-test isolation.
 */
export async function runWithAuthRetry<T>(call: () => Promise<T>, deps: AuthRetryDeps): Promise<T> {
  try {
    return await call()
  }
  catch (e) {
    const statusCode = (e as FetchError)?.statusCode
    if (statusCode !== 401) throw e

    deps.log.debug('access token rejected, refreshing auth and retrying once')
    await deps.reloadAuth()

    if (!deps.getAccessToken()) {
      // Refresh failed: the user store has reset and is navigating to /login.
      // Re-throw the original 401 so the caller maps it to notAuthenticated.
      throw e
    }
    return await call()
  }
}

/**
 * Returns a `withAuthRetry` wrapper for authenticated `$fetch` calls:
 * transparently refreshes the token on 401 and retries the request once.
 */
export function useWithAuthRetry() {
  const log = useLogger('useWithAuthRetry')
  const userStore = useUserStore()

  function withAuthRetry<T>(call: () => Promise<T>): Promise<T> {
    return runWithAuthRetry(call, {
      reloadAuth: () => userStore.reloadAuth(),
      getAccessToken: () => userStore.accessToken,
      log,
    })
  }

  return { withAuthRetry }
}
